import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Navigate, useLocation} from 'react-router-dom';
import {toast} from 'react-toastify';

import {LearnApi} from '../../api/learnApi';
import {LEARN_RETURN_PARAM} from '../../guides/constants';
import {prefetchHelpCenterIdentity} from '../../utils/HelpCenterAttribution';
import {LoadErrorState} from '../elements/LoadErrorState';
import {Spinner} from '../Spinner';
import {SupportChatService} from '../support_chat/SupportChatService';
import {
    applyLearningResponse, beginLearningWrite, getLearningState, registerLearnPage, refreshLearning, STATUS_ERROR,
    STATUS_READY, WRITE_APPLIED, WRITE_FAILED,
} from './learnStore';
import {LearnProfile} from './LearnProfile';
import {LearnRail} from './LearnRail';
import {LearnTrack} from './LearnTrack';
import {celebrationText, nudgeText, taskTitle} from './learnText';
import {
    initialOpenTask, initialOpenTrack, isProfileTrack, needsQueryPolling, STATE_DONE, STATE_PENDING,
    STATE_SKIPPED, taskAfterReturn, trackOfTask, withTaskState,
} from './state/learnModel';
import {guideSeen} from './state/learnMemory';
import {startTarget} from './state/taskDestinations';
import {useLearning} from './useLearning';

// Cada cuánto se vuelve a preguntar con la primera consulta abierta: la consulta pasa en ChatGPT o
// Claude y Dashbo no se entera hasta preguntar (el mismo ritmo que el paso de la IA del túnel).
export const QUERY_POLL_MS = 10000;
// Volviendo de una guía, cuánto se espera la respuesta fresca antes de decidir con lo que haya: un
// pedido colgado no puede dejar la página sin ninguna tarea abierta (review C-rp-fe-aprende-04).
export const RETURN_FRESH_WAIT_MS = 4000;

/**
 * «Aprende Dashbo» (`/aprende`): la capacitación por persona que reemplaza al drawer «Tus primeros
 * pasos» de la 2.1. Mockup aprobado: artefacto 8XsEbw7obtFHjQKJNRJoHk, sección 5.
 *
 * Qué la mantiene al día sin recargar (contrato §6):
 * - el aviso de la app (`notifyOnboardingProgress`), que escucha el store;
 * - al recuperar el foco, porque la consulta a la IA pasa en otra pestaña;
 * - cada 10 s con la primera consulta abierta y pendiente, y sólo con la pestaña a la vista.
 * Cada tarea que se tilda se celebra con `role="status"`, sin mover el foco.
 *
 * Al volver de una guía lanzada desde acá (`?desde=<tarea>`, C-03 del recorrido con Pablo) se abre la
 * tarea que sigue (`taskAfterReturn`), decidida con el estado FRESCO: la copia que había es de antes de
 * que la guía tildara la tarea, y abriría la recién hecha.
 */
export function LearnContent() {
    const {t} = useTranslation();
    const location = useLocation() || {};
    const learning = useLearning();
    const {model, status, celebrating, owner} = learning;
    // Cuántas respuestas había recibido el store al montarse la página: la que vuelva después es fresca.
    const answeredAtMount = useRef(learning.answered || 0);
    const [freshTimedOut, setFreshTimedOut] = useState(false);

    // Lo que se pinta mientras viaja una escritura. Se descarta en cuanto el store trae el estado
    // nuevo: la verdad sigue siendo la del backend.
    const [optimistic, setOptimistic] = useState(null);
    // Las tareas con una escritura en vuelo: un conjunto, porque se puede omitir una y, antes de que
    // conteste, omitir otra (con una sola clave, la segunda liberaba el botón de la primera).
    const [saving, setSaving] = useState(() => new Set());
    const [savingProfile, setSavingProfile] = useState(false);
    const [openTracks, setOpenTracks] = useState([]);
    const [openTask, setOpenTask] = useState(null);
    // El aviso de un Admin se muestra con lo que llegó al entrar: marcarlo visto lo saca del payload,
    // y no puede desaparecer mientras se lo está leyendo.
    const [shownNudge, setShownNudge] = useState(null);
    // La query que ya se aplicó (`null`: todavía nada).
    const [appliedSearch, setAppliedSearch] = useState(null);
    const shown = optimistic || model;
    const search = location.search || '';

    // Qué se abre: al llegar, la tarea pedida por la URL (el aviso de un Admin lleva `?tarea=`) o la
    // próxima, con su pista; después manda la persona. Un `?tarea=` NUEVO estando ya en la página (el
    // link del lanzador: la ruta no remonta si sólo cambia la query) abre esa tarea y suma su pista.
    // Se decide en el render y no en un efecto: con el efecto, el primer pintado salía con todo
    // plegado y un cuadro después se abría. Volviendo de una guía, se espera la respuesta fresca y
    // mientras tanto queda abierta la pista de la tarea de la que se vuelve.
    const params = new URLSearchParams(search);
    const returningFrom = params.get(LEARN_RETURN_PARAM);
    const waitingFresh = !!returningFrom && !freshTimedOut && (learning.answered || 0) <= answeredAtMount.current;
    if (model && model.enabled && waitingFresh && appliedSearch === null && !openTracks.length) {
        const track = trackOfTask(model, returningFrom);
        if (track) setOpenTracks([track.key]);
    }
    if (model && model.enabled && appliedSearch !== search && !waitingFresh) {
        const first = appliedSearch === null;
        const requested = returningFrom ? taskAfterReturn(model, returningFrom) : params.get('tarea');
        setAppliedSearch(search);
        if (first) {
            const task = initialOpenTask(model, requested || (model.nudge && model.nudge.taskKey));
            const track = initialOpenTrack(model, task);
            setOpenTask(task);
            // Suma a lo abierto mientras se esperaba la respuesta fresca (la pista de la tarea de la que
            // se vuelve); al llegar sin volver de nada, no hay nada abierto todavía.
            setOpenTracks((open) => {
                if (!track) return open;
                return open.includes(track) ? open : [...open, track];
            });
            setShownNudge(model.nudge);
        } else if (requested && trackOfTask(model, requested)) {
            const task = initialOpenTask(model, requested);
            const track = trackOfTask(model, task);
            setOpenTask(task);
            setOpenTracks((open) => (open.includes(track.key) ? open : [...open, track.key]));
        }
    }

    useEffect(() => setOptimistic(null), [model]);

    useEffect(() => {
        if (!returningFrom) return undefined;
        const timer = setTimeout(() => setFreshTimedOut(true), RETURN_FRESH_WAIT_MS);
        return () => clearTimeout(timer);
    }, [returningFrom]);

    useEffect(() => registerLearnPage(), []);
    useEffect(() => prefetchHelpCenterIdentity(), []);

    // Al entrar, fresco: el backend registra lo detectado al contestar (contrato §2), y es lo que la
    // persona viene a ver.
    useEffect(() => refreshLearning({fresh: true}), []);

    useEffect(() => {
        const onFocus = () => refreshLearning({fresh: true});
        window.addEventListener('focus', onFocus);
        return () => window.removeEventListener('focus', onFocus);
    }, []);

    const polling = needsQueryPolling(shown, openTask);
    useEffect(() => {
        if (!polling) return undefined;
        const timer = setInterval(() => {
            if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
            refreshLearning({fresh: true});
        }, QUERY_POLL_MS);
        return () => clearInterval(timer);
    }, [polling]);

    // Visto: el backend deja de mandar el aviso (y el lanzador deja de mostrarlo).
    useEffect(() => {
        if (!shownNudge) return;
        const ticket = beginLearningWrite();
        LearnApi.markNudgeSeen().then((response) => applyLearningResponse(response, ticket));
    }, [shownNudge]);

    const openTaskAndTrack = (taskKey, nextModel) => {
        setOpenTask(taskKey);
        const track = taskKey && trackOfTask(nextModel, taskKey);
        if (track) setOpenTracks((open) => (open.includes(track.key) ? open : [...open, track.key]));
    };

    const markSaving = (key, on) => setSaving((current) => {
        const next = new Set(current);
        if (on) next.add(key);
        else next.delete(key);
        return next;
    });

    // Un gesto explícito que falla se avisa y se deshace (rule `custom-components`): dejar pintado un
    // cambio que no se guardó es peor que no haberlo pintado. Y se vuelve a preguntar: si antes salió
    // otra escritura, su respuesta se descartó por vieja y la verdad la tiene el backend.
    const failed = () => {
        setOptimistic(null);
        toast.error(t('learn_dashbo.save_error'));
        refreshLearning({fresh: true});
    };

    const write = (key, request, optimisticState, {advance = false} = {}) => {
        if (!shown) return;
        markSaving(key, true);
        setOptimistic(withTaskState(shown, key, optimisticState));
        const ticket = beginLearningWrite();
        request(key).then((response) => {
            markSaving(key, false);
            const result = applyLearningResponse(response, ticket);
            if (result === WRITE_FAILED) {
                failed();
                return;
            }
            // Omitida o hecha, la tarea deja de pedir atención: se abre la que sigue, según lo que
            // contestó el backend. Una respuesta que se descartó (salió otra después) no mueve nada.
            if (advance && result === WRITE_APPLIED) {
                const next = getLearningState().model;
                openTaskAndTrack(next && next.nextTask, next);
            }
        });
    };

    // El botón de cada tarea: la guía la primera vez, después sólo la pantalla (C-04). Qué guía ya se vio
    // lo anota el motor de guías cuando la DIBUJA (`markDisplayed`), no el clic: una que no llegó (la red,
    // un ancla que no apareció) no se gasta (review C-rp-fe-aprende-01).
    const startOf = (task) => startTarget(task, {guideSeen: guideSeen(owner, task.key)});

    const actions = {
        skip: (key) => write(key, LearnApi.skip, STATE_SKIPPED, {advance: true}),
        unskip: (key) => write(key, LearnApi.unskip, STATE_PENDING),
        complete: (key) => write(key, LearnApi.complete, STATE_DONE, {advance: true}),
        uncomplete: (key) => write(key, LearnApi.uncomplete, STATE_PENDING),
        startOf,
    };

    // Devuelve si se guardó: el perfil deshace su chip si no (ver `LearnProfile`).
    const saveProfile = (roles) => {
        setSavingProfile(true);
        const ticket = beginLearningWrite();
        return LearnApi.saveProfile(roles).then((response) => {
            setSavingProfile(false);
            const result = applyLearningResponse(response, ticket);
            if (result === WRITE_FAILED) failed();
            return result !== WRITE_FAILED;
        });
    };

    const openHelp = () => {
        const agencyName = shown ? shown.agencyName : '';
        const message = openTask
            ? t('learn_dashbo.rail.help_prefill_task', {task: taskTitle(t, openTask, agencyName, shown),
                interpolation: {escapeValue: false}})
            : t('learn_dashbo.rail.help_prefill');
        SupportChatService.showNewMessage(message);
    };

    const celebration = celebrationText(t, celebrating, shown);

    return (
        <section className="section-content ld-screen" data-testid="learn-dashbo">
            <div className="ld-page">
                {/* La región viva existe siempre y se llena al tildarse algo: una que nace con el texto
                    no todos los lectores de pantalla la anuncian. */}
                <div className="ld-celebrate-region" role="status">
                    {celebration && <p className="ld-celebrate" key={celebrating.join()}>{celebration}</p>}
                </div>

                {renderBody()}
            </div>
        </section>
    );

    function renderBody() {
        if (shown && !shown.enabled) return <Navigate to="/agency/clients" replace/>;
        if (!shown && status === STATUS_ERROR) {
            return (
                <LoadErrorState title={t('learn_dashbo.load_error')}
                                text={t('learn_dashbo.load_error_explanation')}
                                retryLabel={t('learn_dashbo.retry')}
                                onRetry={() => refreshLearning({fresh: true})}/>
            );
        }
        if (!shown || status !== STATUS_READY) {
            return (
                <div className="box-loader">
                    <Spinner color="positive"/>
                </div>
            );
        }

        const toggleTrack = (key) => setOpenTracks((open) => (
            open.includes(key) ? open.filter((each) => each !== key) : [...open, key]));
        const toggleTask = (key) => setOpenTask((current) => (current === key ? null : key));

        return (
            <div className="ld-layout">
                <div className="ld-main">
                    <header className="ld-head">
                        <h1 className="ld-head__title">{t('learn_dashbo.title')}</h1>
                        <p className="ld-head__sub">{t('learn_dashbo.subtitle')}</p>
                    </header>

                    {shownNudge && (
                        <p className="ld-nudge">{nudgeText(t, shownNudge, shown.agencyName, shown)}</p>
                    )}

                    <LearnProfile model={shown} saving={savingProfile} onSave={saveProfile}/>

                    {shown.tracks.map((track, index) => (
                        <LearnTrack key={track.key} track={track} model={shown} position={index + 1}
                                    open={openTracks.includes(track.key)}
                                    isProfile={isProfileTrack(shown, track.key)}
                                    agencyName={shown.agencyName}
                                    openTask={openTask} saving={saving}
                                    justDone={celebrating || []}
                                    onToggle={() => toggleTrack(track.key)}
                                    onToggleTask={toggleTask}
                                    actions={actions}/>
                    ))}
                </div>

                <LearnRail model={shown} onOpenHelp={openHelp}/>
            </div>
        );
    }
}
